import {useMemo} from 'react'
import {shallowEqual} from 'react-redux'

import {useAppSelector} from '../../../../../../../../hooks/react-redux'
import {usePipelineIntegrationsOrDraft} from '../../../../../EditPipelinePage/EditPipelinePage.hooks'
import {usePipelineId} from '../../../../../hooks/pipeline'
import type {NewIntegrationDto} from '../../../../../services/pipelinesApi.types'

import {integrations} from './Integrations.consts'

type Integration = NewIntegrationDto & {
  isDeleted: boolean
}

export function useAvailableIntegrations(): Integration[] {
  const allIntegrations = usePipelineIntegrationsOrDraft()
  const id = usePipelineId()
  const deletedIntegrations = useAppSelector(
    state => state.pipelines.pipelineDraft[id]?.deleted?.integrations,
  )
  return useMemo(
    () =>
      allIntegrations
        ?.filter(({type}) => integrations[type] != null)
        .map(integration => ({
          ...integration,
          isDeleted: deletedIntegrations?.includes(integration.id!) ?? false,
        })) ?? [],
    [allIntegrations, deletedIntegrations],
  )
}

const GENERATOR_MAX_ITERATIONS = 1000

export function useGenerateFreeIntegrationName(base: string) {
  const id = usePipelineId()
  const usedNames = useAppSelector(state => {
    const {draft, renamed} = state.pipelines.pipelineDraft[id] ?? {}
    return draft?.integrations?.map(({parameters}) => {
      const {displayName} = parameters
      return renamed?.integrations?.[displayName!] ?? displayName
    })
  }, shallowEqual)
  return () => {
    for (let i = 0; i <= GENERATOR_MAX_ITERATIONS; i++) {
      const name = i > 0 ? `${base} ${i}` : base
      if (!usedNames?.includes(name)) {
        return name
      }
    }
    throw new Error(`Error while generating new integration name`)
  }
}
